// "Open and closed" on the engagement overview: what is still open (the engine's open items, and client requests not yet
// sent) beside what is settled (decisions and milestones from the audit log, and open items resolved or waived), each with
// who and when. Nothing here is computed by the UI beyond sorting; the engine's record is the source.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../app/context";
import { ErrorState, LoadingRows } from "../../components";
import type { OpenItem, PbcIndexRow } from "../../data/types";
import { closedRows, whenText } from "../../lib/closed";
import { useLoad } from "../../lib/hooks";

const SHOW = 8;

export function StatusBoard({ id, items, pbc }: { id: string; items: OpenItem[]; pbc: PbcIndexRow[] }) {
  const { ds } = useApp();
  const log = useLoad(() => ds.table(id, "activity"), [ds, id]);
  const [allOpen, setAllOpen] = useState(false);
  const [allClosed, setAllClosed] = useState(false);
  const open = items.filter((i) => i.status === "open").sort((a, b) => Number(b.level === "before_release") - Number(a.level === "before_release"));
  const drafts = pbc.filter((d) => d.status === "draft").length;
  const closed = log.data ? closedRows(log.data, items) : [];

  return (
    <section aria-labelledby="oc-h" className="rounded-lg border border-line bg-raised">
      <header className="flex items-center justify-between border-b border-line px-4 py-2.5">
        <h2 id="oc-h" className="text-sm font-semibold">Open and closed</h2>
        <Link to={`/e/${id}/activity`} className="text-xs text-brand hover:underline">Full activity</Link>
      </header>
      <div className="grid divide-y divide-line 2xl:grid-cols-2 2xl:divide-x 2xl:divide-y-0">
        <div aria-labelledby="oc-open-h">
          <h3 id="oc-open-h" className="px-4 pt-3 text-xs font-semibold uppercase tracking-wide text-muted">Still open · {open.length + (drafts ? 1 : 0)}</h3>
          <ul className="divide-y divide-line" aria-label="Still open">
            {drafts > 0 && <li className="px-4 py-2 text-sm"><Link to={`/e/${id}/documents`} className="hover:underline">{drafts} client request{drafts === 1 ? "" : "s"} drafted, not sent yet</Link></li>}
            {(allOpen ? open : open.slice(0, SHOW)).map((i) => (
              <li key={i.id} className="px-4 py-2 text-sm">
                {i.level === "before_release" && <span className="mr-1.5 rounded bg-blocked-bg px-1.5 py-0.5 text-[10px] font-semibold uppercase text-blocked">Before release</span>}
                {i.text}
              </li>
            ))}
            {!open.length && !drafts && <li className="px-4 py-4 text-sm text-muted">Nothing open.</li>}
          </ul>
          {open.length > SHOW && <button type="button" className="px-4 pb-3 text-xs text-brand hover:underline" onClick={() => setAllOpen(!allOpen)}>{allOpen ? "Show fewer" : `Show all ${open.length}`}</button>}
        </div>
        <div aria-labelledby="oc-closed-h">
          <h3 id="oc-closed-h" className="px-4 pt-3 text-xs font-semibold uppercase tracking-wide text-muted">Closed and decided · {closed.length}</h3>
          {log.error ? <div className="p-3"><ErrorState error={log.error} onRetry={log.reload} /></div> : !log.data ? <div className="p-3"><LoadingRows rows={3} label="Loading decisions" /></div> : (
            <ul className="divide-y divide-line" aria-label="Closed and decided">
              {(allClosed ? closed : closed.slice(0, SHOW)).map((r) => (
                <li key={r.key} className="px-4 py-2 text-sm">
                  <span className="mr-1.5 text-done" aria-hidden>✓</span><span>{r.text}</span>
                  <span className="block text-xs text-muted">{r.who}{r.at ? ` · ${whenText(r.at)}` : ""}{r.words ? ` · said: “${r.words.length > 80 ? r.words.slice(0, 80) + "…" : r.words}”` : ""}</span>
                </li>
              ))}
              {!closed.length && <li className="px-4 py-4 text-sm text-muted">Nothing decided yet.</li>}
            </ul>
          )}
          {closed.length > SHOW && <button type="button" className="px-4 pb-3 text-xs text-brand hover:underline" onClick={() => setAllClosed(!allClosed)}>{allClosed ? "Show fewer" : `Show all ${closed.length}`}</button>}
        </div>
      </div>
    </section>
  );
}
